import {useCallback, useState} from "react";
import {channel} from "redux-saga";
import {call, select, takeLatest} from "redux-saga/effects";

import {actions, useSagaEffect} from "../store/sagas";
import {useStoreContentList} from "../store/hooks";
import {selectContentList} from "../store/selectors";
import {apiGet} from "./client";

function* loadFirstPage(key, url) {
    const { content, has_more_entries: more } = yield call(apiGet, url);
    yield actions.contentListLoaded(key, content, more);
}

function* loadNextPage(key, url) {
    const { post, comment } = yield select(selectContentList(key));
    const {content, has_more_entries: more} = yield call(apiGet, `${url}&post=${post}&comment=${comment}`);
    yield actions.contentListExtended(key, content, more);
}

function* loadContentList(key, url, actionCh) {
    yield takeLatest(actionCh, function* ({ type }) {
        switch (type) {
            case 'loadNextPage':
                yield call(loadNextPage, key, url);
                break;
            case 'refresh':
                yield call(loadFirstPage, key, url);
                break;
            default:
                throw new Error(type);
        }
    });
    yield takeLatest('userChanged', function* () {
        yield call(loadFirstPage, key, url);
    });
    yield call(loadFirstPage, key, url);
}

export const useProfileOverview = (feedbackCh, username, sort, community) => {
    const key = `u:${username}:${sort}:${community}`;
    const [actionCh] = useState(channel);
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/content/profile.json?user=${username}&sort=${sort}&community=${community}`;
        yield call(loadContentList, key, url, actionCh);
    }, [key, username, community, actionCh]));
    const refresh = useCallback(() => actionCh.put({ type: 'refresh' }), [actionCh]);
    const loadNextPage = useCallback(() => actionCh.put({ type: 'loadNextPage' }), [actionCh]);
    return { ...useStoreContentList(key), refresh, loadNextPage };
};

export const useQueue = (feedbackCh, community, sort, type) => {
    const key = `q:${community}:${sort}:${type}`;
    const [actionCh] = useState(channel);
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/moderate/queue.json?community=${community}&sort=${sort}&type=${type}`;
        yield call(loadContentList, key, url, actionCh);
    }, [key, community, actionCh]));
    const refresh = useCallback(() => actionCh.put({ type: 'refresh' }), [actionCh]);
    const loadNextPage = useCallback(() => actionCh.put({ type: 'loadNextPage' }), [actionCh]);
    return { ...useStoreContentList(key), refresh, loadNextPage };
};


export const useSavedContent = (feedbackCh, folder, community, page) => {
    const [actionCh] = useState(channel);
    const key = `c/${community}/saved:${folder}`;
    useSagaEffect(feedbackCh, useCallback(function* () {
        const path = `/api/v2/folder/get_folder.json?community=${community}&folder=${folder}&page=${page}`;
        yield call(loadContentList, key, path, actionCh);
    }, [key, folder, page, actionCh]));
    const refresh = useCallback(() => actionCh.put({ type: 'refresh' }), [actionCh]);
    const loadNextPage = useCallback(() => actionCh.put({ type: 'loadNextPage' }), [actionCh]);
    return { ...useStoreContentList(key), refresh, loadNextPage };
};